import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { cashReceipts, type CashReceiptListItem } from '@/api/cashDocs';
import { contractors as contractorsApi } from '@/api/catalogs';
import { useGridColumnWidths } from '@/hooks/useGridColumnWidths';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CashReceiptsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Персист ширин колонок ag-Grid (per-grid) — переживає оновлення сторінки.
  const colW = useGridColumnWidths('cash-receipts');
  const list = useQuery({
    queryKey: ['cash-receipts'],
    queryFn: () => cashReceipts.list({ limit: 200 }),
  });
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 500 }),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const cols = useMemo<ColDef<CashReceiptListItem>[]>(() => [
    { field: 'document_number', headerName: t('cd.number'), width: 150 },
    {
      field: 'document_date', headerName: t('cd.date'), width: 170,
      valueFormatter: (p) => p.value ? new Date(p.value as string).toLocaleString(i18n.language) : '',
    },
    {
      field: 'received_from', headerName: t('cr.received_from'), flex: 1, minWidth: 200,
      valueFormatter: (p) => {
        if (p.value) return p.value as string;
        const cId = p.data?.contractor_id;
        return cId ? (cName.get(cId) ?? '') : '';
      },
    },
    { field: 'basis', headerName: t('cd.basis'), flex: 1, minWidth: 200 },
    {
      field: 'amount', headerName: t('cd.amount'), width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => fmt(p.value as string),
    },
    {
      field: 'posted', headerName: t('cd.posted'), width: 130,
      valueFormatter: (p) => (p.value ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no')),
    },
  ], [t, i18n.language, cName]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('cr.title')}</h2>
        <button className="primary" onClick={() => navigate('/cash-receipts/new')}>
          + {t('cr.new')}
        </button>
      </div>
      <div className="ag-theme-quartz grid">
        <AgGridReact<CashReceiptListItem>
          rowData={list.data?.items ?? []}
          columnDefs={cols}
          loading={list.isLoading}
          onGridReady={(e) => { colW.applyTo(e.api); }}
          onColumnResized={colW.onColumnResized}
          onRowDoubleClicked={(e) => e.data && navigate(`/cash-receipts/${e.data.id}`)}
          rowSelection="single"
          getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
        />
      </div>
    </>
  );
}
